/**
 * Backend interface — the single seam between `tento/tento-lona-js/sheets` and the
 * world that fetches / streams / persists rows.
 *
 * Consumers implement this. Ships with a `MemoryRowsBackend` that reads
 * fixture sheets (JSON).
 *
 * Detail: `sheets/.docs/content/rows-backends.md`.
 */

import type { Option } from "../core/option.ts";
import type { SheetNetworkKey } from "../ids/sheet-network-key.ts";
import type { DateTime, NaiveDate, Utc } from "@tento-chrono";

import type { CellEntry, CellValue } from "../core/dtype.ts";
import type { Json } from "../core/json.ts";
import { SdkRequestError } from "../core/errors.ts";
import type { SheetsNetwork } from "../wire/rows-network.ts";
import type { RawRow, RawSheet } from "../wire/raw-sheet-wire.ts";
import type { RowId } from "../ids/row-id.ts";
import { CellId, type SyncKey } from "../ids/ids.ts";
import { RowNetworkKey } from "../ids/row-network-key.ts";
import { SheetId } from "../ids/sheet-id.ts";

/** A subscription's teardown: call it to stop the callbacks the subscribe call registered. */
export type Unsubscribe = () => void;

/**
 * Result of a per-row cell fetch. Two lanes, populated from ONE wire
 * response:
 *   - `cells` — date-anchored cells (the common case: text, data,
 *     events-by-day, weather, tasks).
 *   - `custom` — datetime-positioned cells (`CellEntry.Timepoint`,
 *     carrying a `DateTime.Range`): sticker overlays, scheduling slots.
 *     Empty for rows that carry no datetime cells.
 *
 * Kept as a struct (rather than collapsing datetime cells to day keys,
 * as the legacy `CellEntry[]` return did) so the rows-side substrate
 * can hold both shapes in `canonicalCells` — the seam the
 * `SdkBackendAdapter` docblocks call "sheets grows a timepoint-cell
 * representation".
 */
export interface FetchedCells {
  readonly cells: readonly CellEntry[];
  readonly custom: readonly CellEntry.Timepoint[];
}

/**
 * Server-managed surface marker for the unique header / footer
 * row of a sheet. The body surface is purely synthetic on the
 * client and is never server-backed, so it is not a valid value
 * here. Header and footer each have one anchor row; body is the
 * top-level rows under no anchor.
 */
export type SurfaceKind = "header" | "footer";

/**
 * Opaque per-resource freshness token. Backends mint and consume etags
 * — sheets does not interpret them. Branded `string` so the compiler
 * refuses to mix etags with arbitrary strings or with each other across
 * resources without an explicit cast at the boundary.
 *
 * Optimistic concurrency: a mutation may pass the etag the caller last
 * observed; backends compare and reject (`409`) if the resource has
 * advanced. Reads carry the freshly-minted etag back to the caller.
 *
 * A wire string takes the brand through `Etag.parse` / `Etag.of`.
 */
export type Etag = string & { readonly __brand: "Etag" };

/** An etag (`parse` / `of`). */
export namespace Etag {
  /** `raw` as an `Etag`, or `null` when it is not one (an empty string). */
  export function parse(raw: string): Option<Etag> {
    return raw.length > 0 ? (raw as Etag) : null;
  }

  /** `raw` as an `Etag`; throws when `parse` refuses it. */
  export function of(raw: string): Etag {
    const etag = parse(raw);
    if (etag == null) throw new Error("Etag.of: not an Etag: " + JSON.stringify(raw));
    return etag;
  }
}

/** Row change streamed from a backend (e.g. collab / live-sync). */
export type RowChange =
  | {
      readonly kind: "add";
      readonly row: RawRow;
      readonly position?: number;
      readonly etag?: Etag;
    }
  | {
      readonly kind: "update";
      readonly rowId: RowId;
      readonly patch: Partial<RawRow>;
      readonly etag?: Etag;
    }
  | { readonly kind: "remove"; readonly rowId: RowId; readonly etag?: Etag };

/** Cell change streamed from a backend. */
export type CellChange =
  | { readonly kind: "set"; readonly cell: CellEntry; readonly etag?: Etag }
  | {
      readonly kind: "clear";
      readonly date: NaiveDate.Partial;
      /** The cleared cell's id; absent for a date-keyed cell. */
      readonly id?: CellId;
      readonly etag?: Etag;
    };

/**
 * Rejection of a whole `fetchCellsBatch` the server answered 429 (the
 * per-user / per-IP HTTP rate limit). It is not an access failure: the sheet
 * exists and the caller may read it, the client is just over budget. Callers
 * retry after a pause (`Sheet.fetchCellsBatchPaced`, which also holds the
 * client's sync-status polls for the same window) instead of degrading to a
 * per-row fanout that spends N more requests into the same limit.
 *
 * `retryAfterMs` carries the server's `Retry-After` when the transport can see
 * it; `null` means unknown and the caller picks its own backoff.
 *
 * An `SdkRequestError` (boundary `"rows-http"`, step `"get-cells-batch"`,
 * outcome `"refused"`, status 429, reason `"rate_limited"`), so one
 * `instanceof SdkRequestError` covers it beside every other refused request.
 */
export class BatchFetchThrottledError extends SdkRequestError<SdkRequestError.Refused> {
  declare readonly boundary: "rows-http";
  declare readonly step: "get-cells-batch";

  constructor(
    readonly sheetKey: SheetNetworkKey,
    readonly retryAfterMs: Option<number>,
  ) {
    super({
      boundary: "rows-http",
      step: "get-cells-batch",
      method: "POST",
      path: "/api/sheets/{sheetKey}/cells/batch",
      failure: { outcome: "refused", status: 429 },
      reason: "rate_limited",
      details: `sheet "${sheetKey}" throttled`,
    });
    this.name = "BatchFetchThrottledError";
  }
}

/**
 * Per-row outcome of a batched cell fetch. Mirrors the wire
 * `SheetsNetwork.BatchCellResult` shape but uses canonical `CellEntry`
 * values (post-encoding-decode) on the success path.
 *
 * One bad rowKey (expired Google auth, deleted calendar, foreign-user
 * probe) yields its own non-`ok` status — it does **not** poison the
 * other rows in the batch. Callers should treat the map entry as the
 * authoritative per-row outcome.
 */
export type BatchRowCells =
  | {
      readonly status: "ok";
      readonly rowKey: RowNetworkKey;
      /** The row's metadata as the server sent it (its `encoding`, `shape`, …). */
      readonly metadata: Option<Json>;
      readonly cells: readonly CellEntry[];
      /** Non-null when a background sync is in flight for this row; the
       *  client polls these keys and re-fetches on completion. Only the HTTP
       *  backend has background sync — every other backend writes `null`. */
      readonly syncKeys: Option<readonly SyncKey[]>;
    }
  | {
      readonly status: "forbidden" | "not_found" | "error";
      readonly rowKey: RowNetworkKey;
      readonly error: string;
    };

/**
 * Read + write surface with live-event streams.
 *
 * Mutations mirror the `RowChange` / `CellChange` vocabulary one-for-one —
 * `writeCell` / `clearCell` produce the corresponding `CellChange`, and
 * `addRow` / `updateRow` / `deleteRow` produce the corresponding
 * `RowChange`. Implementations decide whether a local mutation also
 * fans out through the `onRowChange` / `onCellChange` subscribers
 * (the in-memory `MemoryRowsBackend` echoes; a networked backend may defer
 * until the server confirms).
 */
export interface Backend {
  // ── Reads ───────────────────────────────────────────────────────────

  /** Fetch the wire-format `RawSheet` that `RawSheet.hydrate` consumes.
   *  Returns `null` when the server has no persisted record for
   *  `sheetKey` — typically a reserved lookup key (e.g. `:~lona:home`)
   *  that the user hasn't promoted. Callers fall back to a local
   *  catalog (see `SheetModelCache.open`). */
  fetchSheet(sheetKey: SheetNetworkKey): Promise<Option<RawSheet>>;

  fetchCells(rowId: RowId, range: NaiveDate.Range): Promise<FetchedCells>;

  /**
   * The cells stored under `key` — a row id, a lookup key or an alias
   * data owner's key, in its wire spelling — over `range`, as
   * `fetchCells` answers them. Independent of any row of this sheet:
   * the read names its row by network key alone.
   */
  fetchCellsByKey(key: RowNetworkKey, range: NaiveDate.Range): Promise<FetchedCells>;

  /**
   * Every row of `sheetKey` that holds cells over `range`, with those
   * cells, in one round trip: the server enumerates the rows, unlike
   * `fetchCellsBatch`, whose caller names them. Each answer row is the
   * wire `SheetRowDataResponse` — its id, lookup key, label, row type and
   * metadata, its cells in their wire time shape (a datetime cell keeps
   * its `endDate`) with values undecoded, and its `syncKeys` (non-null
   * while a background sync for the row is in flight; only the HTTP
   * backend has background sync). The consumer decodes the cells once.
   */
  fetchSheetCells(
    sheetKey: SheetNetworkKey,
    range: NaiveDate.Range,
  ): Promise<SheetsNetwork.SheetRowDataResponse[]>;

  /**
   * Batch fetch cells for a client-supplied rowKey list, scoped to a
   * sheet the caller has access to. Required for aggregate rows
   * (calendar-list, task-list) whose dynamic children come from
   * client-side state and aren't enumerable from sheet membership
   * alone.
   *
   * Returns a `Map<rowKey, BatchRowCells>`. Map order is not
   * meaningful; callers should index by rowKey. Per-row failures are
   * carried as non-`ok` entries — the promise rejects only for
   * whole-batch failures (sheet ACL denied, oversize request,
   * transport error). A 429 rejects as `BatchFetchThrottledError`, never as an
   * access failure.
   */
  fetchCellsBatch(
    sheetKey: SheetNetworkKey,
    rowKeys: readonly RowNetworkKey[],
    range: NaiveDate.Range,
  ): Promise<Map<RowNetworkKey, BatchRowCells>>;

  // ── Subscriptions ───────────────────────────────────────────────────

  onRowChange(handler: (change: RowChange) => void): Unsubscribe;

  onCellChange(
    rowId: RowId,
    handler: (change: CellChange) => void,
  ): Unsubscribe;

  // ── Writes ──────────────────────────────────────────────────────────

  /**
   * Set one cell. Mirrors `CellChange { kind: "set" }`.
   *
   * `expectedEtag`, when non-null, is the etag the caller last observed
   * for this cell's resource. Backends supporting optimistic concurrency
   * reject the mutation (typically with a `409`) when the server has
   * advanced past it. Pass `null` to mean "no etag known — server may
   * overwrite". Every write takes `expectedEtag` with the same meaning.
   *
   * `requestId`, when given, is a durable write's own id: a backend that
   * debounces the write hands it to the executor (`DebounceOptions.requestId`)
   * so a refusal names exactly the writes its execution carried. A backend
   * that writes in memory, or sends at once, ignores it.
   */
  writeCell(
    rowId: RowId,
    cell: CellEntry,
    expectedEtag: Option<Etag>,
    requestId: Option<string>,
  ): Promise<void>;

  /**
   * Clear one cell. `id` disambiguates within `multi`-cardinality sources;
   * `null` for `single`-cardinality. Mirrors `CellChange { kind: "clear" }`.
   */
  clearCell(
    rowId: RowId,
    date: NaiveDate.Partial,
    id: Option<CellId>,
    expectedEtag: Option<Etag>,
  ): Promise<void>;

  /**
   * Create or overwrite a datetime-positioned (custom) cell keyed by a
   * `DateTime.Range`. `replaceCellId` requests the server's atomic move
   * — the old cell (by raw id) is deleted before the insert. The server
   * is the id authority, so it returns canonical changed cells the caller
   * reconciles by id (`SourceRow.writeTimepointCell`). The sticker / scheduling
   * write seam.
   */
  writeTimepointCell(
    rowId: RowId,
    dateRange: DateTime.Range<Utc>,
    data: Option<Json | CellValue>,
    replaceCellId: Option<CellId>,
    expectedEtag: Option<Etag>,
  ): Promise<SheetsNetwork.CellWriteResponse>;

  /** Delete a datetime (custom) cell by its raw cell id. */
  clearTimepointCell(
    rowId: RowId,
    cellId: CellId,
    expectedEtag: Option<Etag>,
  ): Promise<SheetsNetwork.CellWriteResponse>;

  /**
   * Insert a new row. Mirrors `RowChange { kind: "add" }`.
   *
   * `sheetId` links the row to its sheet (required for sheet-linked rows — the
   * rows-side `Sheet` passes `this.id`). When `row.parentId` is set, the backend
   * links the new row as a CHILD of that parent. Returns the
   * SERVER-AUTHORITATIVE row id when the backend mints one (the caller rekeys
   * its local row to it); `null` when the backend keeps the supplied id
   * (in-memory backends).
   */
  addRow(
    row: RawRow,
    sheet: Option<SheetId>,
    position: Option<number>,
    expectedEtag: Option<Etag>,
    surface: Option<SurfaceKind>,
  ): Promise<Option<RowId>>;

  /**
   * Patch the row's own fields (`PATCH /api/rows/{rowKey}`: `label`, `icon`,
   * `formula`, `lookupKey`, `timeScale`, `heightPx`, `attributes` — each set,
   * or cleared with `null`; `attributes` replaced whole). Mirrors
   * `RowChange { kind: "update" }`. Resolves with the server's answer
   * (`Backend.RowUpdated`: whether the row's cells changed with the write);
   * rejects when the write was refused or went unanswered. The one transport
   * of a row's field writes: every write of the client's row-field ledger
   * (`RowWrites`) is sent through it.
   */
  updateRow(
    rowId: RowId,
    patch: Backend.UpdateRowBody,
    expectedEtag: Option<Etag>,
  ): Promise<Backend.RowUpdated>;

  /** Remove a row. Mirrors `RowChange { kind: "remove" }`. */
  deleteRow(rowId: RowId, expectedEtag: Option<Etag>): Promise<void>;
}

/** Types that go with `Backend`: `UpdateRowBody`, `RowUpdated`. */
export namespace Backend {
  /** What `updateRow` writes: the row PATCH body (`null` clears a field). */
  export type UpdateRowBody = SheetsNetwork.PatchRow_PendingValidation;

  /**
   * The server's answer to `updateRow`: `shouldRefetchData` — the write
   * changed what the row's cells are (an alias-affecting attribute, e.g. a
   * weather location), so a cached copy of them is stale.
   */
  export type RowUpdated = { readonly shouldRefetchData: boolean };
}
